import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { GuildProvider } from './contexts/GuildContext';
import { ToastProvider } from './contexts/ToastContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import Toast from './components/Toast';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import DashboardPage from './pages/DashboardPage';
import RulesPage from './pages/RulesPage';
import FiltersPage from './pages/FiltersPage';
import PunishmentPage from './pages/PunishmentPage';
import HistoryPage from './pages/HistoryPage';
import WhitelistPage from './pages/WhitelistPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 30초마다 자동 리프레시
      refetchInterval: 30000,
      // 윈도우 포커스 시 리프레시 비활성화
      refetchOnWindowFocus: false,
      // 네트워크 재연결 시 리프레시
      refetchOnReconnect: true,
      // 재시도 로직: 최대 3번, 지수 백오프
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // 5분간 캐시 유지
      staleTime: 5 * 60 * 1000,
      // 10분 후 가비지 컬렉션
      gcTime: 10 * 60 * 1000,
    },
    mutations: {
      // 뮤테이션 재시도: 최대 2번
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <GuildProvider>
            <ToastProvider>
              <BrowserRouter>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <DashboardPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/rules"
                    element={
                      <ProtectedRoute>
                        <RulesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/filters"
                    element={
                      <ProtectedRoute>
                        <FiltersPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/punishments"
                    element={
                      <ProtectedRoute>
                        <PunishmentPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/history"
                    element={
                      <ProtectedRoute>
                        <HistoryPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/whitelist"
                    element={
                      <ProtectedRoute>
                        <WhitelistPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                </Routes>
                <Toast />
              </BrowserRouter>
            </ToastProvider>
          </GuildProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
